'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import {
  FACE_CLAIM_AGE_GROUPS,
  AGE_GROUP_LABELS,
  type FaceClaimAgeGroup,
} from 'types/faceClaimAgeGroups';

const AGE_GROUP_OPTIONS: FaceClaimAgeGroup[] = [
  FACE_CLAIM_AGE_GROUPS.ALL,
  FACE_CLAIM_AGE_GROUPS['18-23'],
  FACE_CLAIM_AGE_GROUPS['24-31'],
  FACE_CLAIM_AGE_GROUPS['32-41'],
  FACE_CLAIM_AGE_GROUPS['42-60'],
];

type Props = {
  currentAgeGroup: FaceClaimAgeGroup;
};

export default function AgeFilterDropdown({ currentAgeGroup }: Props) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const handleSelect = useCallback(
    (group: FaceClaimAgeGroup) => {
      setOpen(false);
      // Always navigate to page 1 when changing filter
      const basePath = '/face-claims';
      const params = new URLSearchParams();
      if (group !== 'all') {
        params.set('age', group);
      }
      const query = params.toString();
      const url = query ? `${basePath}?${query}` : basePath;
      router.push(url);
    },
    [router]
  );

  const handleOutsideClick = useCallback((e: MouseEvent | TouchEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [open, handleOutsideClick]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Filter by age group"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg',
          'bg-[#232323] hover:bg-[#2a2a2a] border border-gray-700/50',
          'text-sm font-medium text-gray-100 transition-colors'
        )}
      >
        <span>{AGE_GROUP_LABELS[currentAgeGroup]}</span>
        <ChevronDown
          className={clsx('w-4 h-4 text-gray-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      <div
        role="listbox"
        className={clsx(
          'absolute left-0 top-full mt-1.5 z-20 min-w-[180px]',
          'bg-[#232323] border border-gray-700/50 rounded-lg shadow-xl shadow-black/30',
          'py-1 overflow-hidden',
          open ? 'block' : 'hidden'
        )}
      >
        {AGE_GROUP_OPTIONS.map((group) => (
          <button
            key={group}
            type="button"
            role="option"
            aria-selected={group === currentAgeGroup}
            onClick={() => handleSelect(group)}
            className={clsx(
              'w-full px-4 py-2.5 text-left text-sm transition-colors',
              group === currentAgeGroup
                ? 'bg-orange-500/20 text-orange-400 font-medium'
                : 'text-gray-300 hover:bg-gray-800/80 hover:text-gray-100'
            )}
          >
            {AGE_GROUP_LABELS[group]}
          </button>
        ))}
      </div>
    </div>
  );
}
